import { useEffect, useState } from "react";
import { History, Loader2, PlusCircle, ArrowRight, DollarSign, UserCheck, FileEdit, Hand } from "lucide-react";
import { api, formatDate, formatRp } from "@/lib/api";
import { statusMeta } from "@/lib/api";

const actionMeta = {
  created: { icon: PlusCircle, color: "text-blue-600 bg-blue-50", label: "Order dibuat" },
  status_changed: { icon: ArrowRight, color: "text-amber-600 bg-amber-50", label: "Status diubah" },
  cost_updated: { icon: DollarSign, color: "text-emerald-600 bg-emerald-50", label: "Biaya final diperbarui" },
  assigned_changed: { icon: UserCheck, color: "text-purple-600 bg-purple-50", label: "Teknisi di-assign" },
  diagnosis_updated: { icon: FileEdit, color: "text-slate-600 bg-slate-100", label: "Diagnosa diperbarui" },
  claimed: { icon: Hand, color: "text-emerald-700 bg-emerald-50", label: "Diambil teknisi" },
};

export default function AuditLog({ serviceId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/services/${serviceId}/events`)
      .then((r) => setEvents(r.data))
      .finally(() => setLoading(false));
  }, [serviceId]);

  const renderDetail = (e) => {
    const d = e.details || {};
    if (e.action === "status_changed") {
      return (
        <span className="text-xs">
          <span className={`px-1.5 py-0.5 rounded border ${statusMeta[d.from]?.cls || ""}`}>{statusMeta[d.from]?.label || d.from}</span>
          <ArrowRight className="w-3 h-3 inline mx-1" />
          <span className={`px-1.5 py-0.5 rounded border ${statusMeta[d.to]?.cls || ""}`}>{statusMeta[d.to]?.label || d.to}</span>
        </span>
      );
    }
    if (e.action === "cost_updated") {
      return <span className="text-xs text-slate-600">{formatRp(d.from)} → <span className="font-semibold text-slate-900">{formatRp(d.to)}</span></span>;
    }
    if (e.action === "assigned_changed") {
      return <span className="text-xs text-slate-600">→ {d.to_name || "Belum ditugaskan"}</span>;
    }
    if (e.action === "claimed") {
      return <span className="text-xs text-slate-600">Oleh {d.tech_name}{d.auto_status ? ` · status → ${statusMeta[d.auto_status]?.label}` : ""}</span>;
    }
    if (e.action === "created") {
      return <span className="text-xs text-slate-600">Tiket #{d.ticket_no}</span>;
    }
    return null;
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid="audit-log">
      <div className="flex items-center gap-2 mb-4">
        <History className="w-4 h-4 text-blue-600" />
        <h3 className="font-display font-semibold text-slate-900">Riwayat Perubahan ({events.length})</h3>
      </div>
      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : events.length === 0 ? (
        <div className="text-sm text-slate-500 text-center py-4">Belum ada perubahan tercatat.</div>
      ) : (
        <div className="relative">
          <div className="absolute left-3 top-1 bottom-1 w-px bg-slate-200" />
          <div className="space-y-3">
            {events.map((e) => {
              const meta = actionMeta[e.action] || { icon: FileEdit, color: "text-slate-600 bg-slate-100", label: e.action };
              const Icon = meta.icon;
              return (
                <div key={e.id} className="relative pl-8" data-testid={`event-${e.action}`}>
                  <div className={`absolute left-0 top-0 w-6 h-6 rounded-full flex items-center justify-center ${meta.color}`}>
                    <Icon className="w-3.5 h-3.5" />
                  </div>
                  <div className="text-sm font-medium text-slate-900 flex items-center gap-2 flex-wrap">
                    {meta.label} {renderDetail(e)}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {e.actor_name} <span className="text-slate-400">·</span> {formatDate(e.created_at)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
